import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { Loader } from '@mantine/core';
import { Alert, Button, Checkbox, Input, Modal } from '@shared/ui';
import { usePickupPoints } from '@modules/pickup-points';
import { productsApi } from '../../api/products.api';
import type { Product } from '../../types';
import './ManageProductStocksModal.css';

type Row = {
  pickupPointId: number;
  quantity: number;
  unlimitedStock: boolean;
};

interface ManageProductStocksModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Pick<Product, 'id' | 'name' | 'sellerId' | 'pickupStocks'>;
  onSaved?: () => void | Promise<void>;
  /** Подсказка после создания товара: указать остатки по точкам */
  afterCreateHint?: boolean;
  /** Подсказка перед показом автоскрытого / OOS товара */
  beforeShowHint?: boolean;
}

export default function ManageProductStocksModal({
  isOpen,
  onClose,
  product,
  onSaved,
  afterCreateHint = false,
  beforeShowHint = false,
}: ManageProductStocksModalProps) {
  const queryClient = useQueryClient();
  const { data: pickupPoints = [], isLoading: pickupLoading } = usePickupPoints();
  const [rows, setRows] = useState<Row[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !pickupPoints.length) return;
    const list = product.pickupStocks ?? [];
    const byPid = new Map(list.map((s) => [s.pickupPointId, s]));
    setRows(
      pickupPoints.map((pp) => ({
        pickupPointId: pp.id,
        quantity: byPid.get(pp.id)?.quantity ?? 0,
        unlimitedStock: Boolean(byPid.get(pp.id)?.unlimitedStock),
      })),
    );
  }, [isOpen, pickupPoints, product.id, product.pickupStocks]);

  const handleSave = async () => {
    if (!rows.length) {
      notifications.show({
        title: 'Нет точек',
        message: 'Создайте точки самовывоза в разделе контактов',
        color: 'orange',
      });
      return;
    }
    if (beforeShowHint) {
      const hasAvailable = rows.some((r) => r.unlimitedStock || r.quantity > 0);
      if (!hasAvailable) {
        notifications.show({
          title: 'Укажите остатки',
          message:
            'Чтобы показать товар, укажите количество хотя бы на одной точке или включите «Без ограничений».',
          color: 'orange',
        });
        return;
      }
    }
    setSaving(true);
    try {
      await productsApi.updatePickupStocks(
        product.id,
        rows.map((r) => ({
          pickupPointId: r.pickupPointId,
          quantity: Math.max(0, Number(r.quantity) || 0),
          unlimitedStock: r.unlimitedStock,
        })),
      );
      queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections', 'out-of-stock'] });
      queryClient.invalidateQueries({ queryKey: ['products', 'collections', 'counts'] });
      queryClient.invalidateQueries({ queryKey: ['product', product.id] });
      queryClient.invalidateQueries({ queryKey: ['pickup-points'] });
      if (!beforeShowHint) {
        notifications.show({
          title: 'Сохранено',
          message: 'Остатки по точкам обновлены',
          color: 'green',
        });
      }
      await onSaved?.();
      onClose();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : e instanceof Error
            ? e.message
            : 'Не удалось сохранить';
      notifications.show({
        title: 'Ошибка',
        message: msg ?? 'Не удалось сохранить',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  if (product.sellerId == null) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Остатки по точкам" maxWidth={520}>
        <Alert tone="warning">Учёт остатков по точкам только для товаров продавца.</Alert>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Остатки: ${product.name}`} maxWidth={560}>
      {pickupLoading ? (
        <div className="stocks-modal__loader">
          <Loader size="sm" />
        </div>
      ) : pickupPoints.length === 0 ? (
        <Alert tone="warning">Сначала создайте точки самовывоза.</Alert>
      ) : (
        <div className="stocks-modal">
          {afterCreateHint ? (
            <Alert tone="info">
              Укажите количество по каждой точке самовывоза — так товар сразу будет доступен к заказу.
            </Alert>
          ) : null}

          {beforeShowHint ? (
            <Alert tone="info">
              Товар был скрыт, так как долго не было в наличии. Укажите остатки по точкам, чтобы снова
              показать его в каталоге.
            </Alert>
          ) : null}

          <p className="stocks-modal__hint">
            «Без ограничений» — остаток не уменьшается при заказах. Если везде ноль и безлимит выключен,
            товар помечается как не в наличии.
          </p>

          <div className="stocks-modal__list">
            {rows.map((row, index) => {
              const pp = pickupPoints.find((p) => p.id === row.pickupPointId);
              const label = pp ? `${pp.city}, ${pp.address}` : `Точка #${row.pickupPointId}`;
              return (
                <div key={row.pickupPointId} className="stocks-modal__row">
                  <div className="stocks-modal__row-title">{label}</div>
                  <div className="stocks-modal__row-controls">
                    <div className="stocks-modal__qty">
                      <Input
                        type="number"
                        label="Количество"
                        min={0}
                        value={row.unlimitedStock ? '' : row.quantity}
                        placeholder="0"
                        disabled={row.unlimitedStock}
                        onChange={(e) => {
                          const n = Math.max(0, Number(e.currentTarget.value) || 0);
                          setRows((prev) =>
                            prev.map((r, i) => (i === index ? { ...r, quantity: n } : r)),
                          );
                        }}
                      />
                    </div>
                    <div className="stocks-modal__unlimited">
                      <Checkbox
                        label="Без ограничений"
                        checked={row.unlimitedStock}
                        onChange={(e) => {
                          const checked = e.currentTarget.checked;
                          setRows((prev) =>
                            prev.map((r, i) =>
                              i === index ? { ...r, unlimitedStock: checked } : r,
                            ),
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="stocks-modal__actions">
            <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
              Отмена
            </Button>
            <Button type="button" onClick={handleSave} loading={saving}>
              {beforeShowHint ? 'Сохранить и показать' : 'Сохранить'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
